import { useEffect, useState, type MouseEvent } from 'react';
import { NavLink, Outlet } from 'react-router';
import { clsx } from 'clsx';
import { useAuth } from '../auth/useAuth.js';
import { useGuardedNavigate } from '../navigation/GuardContext.js';
import { Button } from '../ui/Button.js';
import { Logo } from '../components/Logo.js';
import { AccountMenu } from './AccountMenu.js';
import { CommandPalette } from './CommandPalette.js';
import { ThemeToggle } from './ThemeToggle.js';
import { useSearchIndex } from './useSearchIndex.js';

/*
 * Three items, deliberately. Test a URL, the audit log and settings are not
 * daily traffic work, so they live in the account menu; the domain liveness
 * list is gone from the UI entirely. Every one of those routes still resolves
 * — this is a change to what the header offers, not to what the app serves.
 */
const NAV = [
  { to: '/groups', label: 'Groups' },
  { to: '/urls', label: 'URLs' },
  { to: '/reports', label: 'Reports' },
];

/**
 * Whether a click should be handled in-app.
 *
 * A modified click — Cmd, Ctrl, Shift, Alt, or the middle button — is the
 * operator asking the browser for a new tab or window, and must be left alone;
 * intercepting it would break the one gesture that opens a second view of the
 * console. Only a plain left click routes through the navigation guard.
 */
function isPlainClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

export function AppShell() {
  const { user, signOut } = useAuth();
  /* Guarded, not plain: a screen holding an unsaved draft — the URL page's
     weight editor — registers a veto, and the nav and the palette are exits it
     cannot see for itself. */
  const navigate = useGuardedNavigate();
  const { items } = useSearchIndex();
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="min-h-screen bg-surface">
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <header className="border-b border-rule bg-surface-raised">
        <div className="mx-auto flex max-w-[84rem] items-center justify-between gap-(--space-4) px-(--space-5) py-(--space-3)">
          {/* The mark, then the name. The company line that used to sit above
              the name is gone: it named the operator's own employer, which
              nobody signing in to their own console needs telling. */}
          <div className="flex items-center gap-(--space-2)">
            <Logo size={26} />
            <span className="font-bold">Redirector Engine</span>
          </div>

          <div className="flex items-center gap-(--space-3)">
            <Button size="sm" variant="ghost" onClick={() => setPaletteOpen(true)}>
              Search <kbd className="text-[length:var(--text-micro)] text-ink-subtle">⌘K</kbd>
            </Button>
            <ThemeToggle />
            {user !== null && (
              <AccountMenu
                email={user.email}
                role={user.role}
                onSignOut={() => void signOut()}
              />
            )}
          </div>
        </div>

        <nav aria-label="Main navigation" className="mx-auto max-w-[84rem] px-(--space-5)">
          <ul className="scroll-x flex gap-(--space-1)">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={(event) => {
                    if (isPlainClick(event)) {
                      event.preventDefault();
                      navigate(item.to);
                    }
                  }}
                  className={({ isActive }) =>
                    clsx(
                      'inline-block border-b-2 px-(--space-3) py-(--space-2) text-[length:var(--text-small)] transition-colors duration-(--duration-fast)',
                      /* Weight as well as colour: the underline and the
                         heavier type both say "here", so the active item
                         survives a monochrome screen. */
                      isActive
                        ? 'border-b-accent font-semibold text-ink'
                        : 'border-b-transparent text-ink-muted hover:text-ink',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <main id="main" className="mx-auto max-w-[84rem] px-(--space-5) py-(--space-6)">
        <Outlet />
      </main>

      <CommandPalette
        open={paletteOpen}
        items={items}
        onClose={() => setPaletteOpen(false)}
        onNavigate={(href) => navigate(href)}
      />
    </div>
  );
}
